<template>
  <div class="training-yearly-plan-surface">
    <div
      v-if="!rows.length"
      class="training-yearly-plan-filter-empty"
    >
      <v-icon color="#90A4AE">filter_alt_off</v-icon>
      <span>{{ $t("wkt.admin.training-action.yearly-plan.filtered-empty") }}</span>
    </div>

    <div v-else class="training-yearly-plan-scroll">
      <div class="training-yearly-plan-grid" :style="timelineGridStyle">
        <div class="training-yearly-plan-header">
          <div class="training-yearly-plan-label-cell training-yearly-plan-label-cell--header" />
          <div class="training-yearly-plan-months">
            <button
              v-for="segment in monthSegments"
              :key="`month-${segment.monthIndex}`"
              type="button"
              class="training-yearly-plan-month"
              :style="segment.style"
              @click="onMonthClick(segment.monthIndex)"
            >
              <span>{{ segment.label }}</span>
              <small>{{ segment.subtitle }}</small>
            </button>
          </div>
        </div>

        <div
          v-for="row in rows"
          :key="`row-${row.id}`"
          class="training-yearly-plan-row"
          :style="`height: ${row.height}px;`"
        >
          <button
            type="button"
            class="training-yearly-plan-label-cell"
            :style="`height: ${row.height}px;`"
            @click="$emit('category-click', { categoryId: row.id, originalEvent: $event })"
          >
            <span
              class="training-yearly-plan-label-chip"
              :style="`background: ${row.color};`"
            />
            <span class="training-yearly-plan-label-text">{{ row.name }}</span>
            <span class="training-yearly-plan-label-count">{{ row.items.length }}</span>
          </button>

          <div class="training-yearly-plan-row-body">
            <div
              v-for="segment in monthSegments"
              :key="`month-bg-${row.id}-${segment.monthIndex}`"
              class="training-yearly-plan-month-bg"
              :class="{
                'training-yearly-plan-month-bg--alt': segment.alt,
                'training-yearly-plan-month-bg--quarter': segment.isQuarterStart,
              }"
              :style="segment.style"
            />

            <div
              v-if="showTodayMarker"
              class="training-yearly-plan-today-line"
              :style="`left: ${todayOffsetPercent}%; height: ${row.height}px;`"
            >
              <span v-if="row.id === rows[0].id" class="training-yearly-plan-today-label">
                {{ $t("wkt.admin.training-action.yearly-plan.today") }}
              </span>
            </div>

            <v-tooltip
              v-for="item in row.items"
              :key="`item-${item.id}`"
              bottom
              open-delay="200"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  class="training-yearly-plan-item"
                  :class="{
                    'training-yearly-plan-item--single-day': item.isSingleDay,
                    'training-yearly-plan-item--canceled': item.canceled,
                  }"
                  :style="getItemStyle(item, row)"
                  tabindex="0"
                  role="button"
                  :aria-label="getItemAriaLabel(item, row)"
                  v-on="on"
                  @click="$emit('item-click', { trainingActionId: item.id })"
                  @keydown.enter.prevent="$emit('item-click', { trainingActionId: item.id })"
                  @keydown.space.prevent="$emit('item-click', { trainingActionId: item.id })"
                >
                  <span v-if="item.startsBeforeRange" class="training-yearly-plan-item-arrow">←</span>
                  <span v-if="showItemLabel(item)" class="training-yearly-plan-item-text">{{ item.name }}</span>
                  <span v-if="item.endsAfterRange" class="training-yearly-plan-item-arrow">→</span>
                  <span
                    v-if="item.isSingleDay"
                    class="training-yearly-plan-item-dot"
                    :style="`background: ${row.color};`"
                  />
                </div>
              </template>

              <div style="max-width: 320px;">
                <div style="font-weight: 600; padding-bottom: 4px;">{{ item.name }}</div>
                <div v-if="item.courseName" style="font-size: 12px; color: #546E7A; padding-bottom: 6px;">
                  {{ item.courseName }}
                </div>
                <div>{{ formatItemDates(item) }}</div>
                <div>{{ row.name }}</div>
                <div>{{ resolveStatusLabel(item.status) }}</div>
                <div>{{ resolveModeLabel(item.mode) }}</div>
                <div>
                  {{ $t("wkt.admin.training-action.yearly-plan.tooltip.enrollments") }}:
                  {{ item.enrolledCount || 0 }} / {{ item.maxSeats == null ? $t("wkt.admin.training-action.yearly-plan.tooltip.no-limit") : item.maxSeats }}
                </div>
              </div>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { buildRange, getOffsetPercent, getWidthPercent, packLanes, parseIsoDate } from "./layout";
import { alphaColor, colorFor, mixWithWhite } from "./palette";

const DAY_MS = 24 * 60 * 60 * 1000;
const LABELS_WIDTH = 260;
const MIN_TIMELINE_WIDTH = 1200;
const LANE_HEIGHT = 34;
const LANE_PADDING = 22;
const MIN_ROW_HEIGHT = 78;

export default {
  name: "TrainingYearlyTimeline",
  props: {
    year: {
      type: Number,
      required: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({
        categoryIds: [],
        statuses: [],
        modes: [],
        showCanceled: false,
      }),
    },
    zoomMonth: {
      type: Number,
      default: null,
    },
    today: {
      type: [Date, String],
      default: () => new Date(),
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    modeOptions: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    range() {
      return buildRange(this.year, this.zoomMonth);
    },
    timelineGridStyle() {
      return `min-width: ${LABELS_WIDTH + MIN_TIMELINE_WIDTH}px;`;
    },
    normalizedToday() {
      if (this.today instanceof Date) {
        return new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate(), 12, 0, 0, 0);
      }

      return parseIsoDate(this.today) || new Date();
    },
    showTodayMarker() {
      return this.normalizedToday.getTime() >= this.range.start.getTime()
        && this.normalizedToday.getTime() <= this.range.end.getTime();
    },
    todayOffsetPercent() {
      return getOffsetPercent(this.normalizedToday, this.range);
    },
    filteredItems() {
      return (this.items || []).filter((item) => {
        if (!item) {
          return false;
        }

        if (!this.filters.showCanceled && item.canceled) {
          return false;
        }

        if (this.filters.categoryIds.length && !this.filters.categoryIds.includes(item.categoryId)) {
          return false;
        }

        if (this.filters.statuses.length && !this.filters.statuses.includes(item.status)) {
          return false;
        }

        if (this.filters.modes.length && !this.filters.modes.includes(item.mode)) {
          return false;
        }

        return true;
      });
    },
    rows() {
      const itemsByCategoryId = this.filteredItems.reduce((accumulator, item) => {
        if (!accumulator[item.categoryId]) {
          accumulator[item.categoryId] = [];
        }

        accumulator[item.categoryId].push(item);
        return accumulator;
      }, {});

      return (this.categories || [])
        .map((category) => {
          const packed = packLanes(itemsByCategoryId[category.id] || [], this.range);
          if (!packed.itemsWithLane.length) {
            return null;
          }

          const color = colorFor(category.colorIndex);

          return {
            ...category,
            color,
            textColor: "#17324d",
            items: packed.itemsWithLane,
            laneCount: packed.laneCount,
            height: Math.max((packed.laneCount * LANE_HEIGHT) + LANE_PADDING, MIN_ROW_HEIGHT),
          };
        })
        .filter((row) => !!row);
    },
    monthSegments() {
      if (this.zoomMonth !== null && this.zoomMonth !== undefined) {
        const date = new Date(this.year, this.zoomMonth, 1, 12, 0, 0, 0);
        return [this.buildMonthSegment(date, false, true)];
      }

      return Array.from({ length: 12 }, (_, monthIndex) => {
        const date = new Date(this.year, monthIndex, 1, 12, 0, 0, 0);
        return this.buildMonthSegment(date, monthIndex % 2 === 1, monthIndex % 3 === 0);
      });
    },
  },
  methods: {
    buildMonthSegment(date, alt, isQuarterStart) {
      const start = new Date(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0, 0);
      const left = getOffsetPercent(start, this.range);
      const width = getWidthPercent(start, end, this.range);
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "en";

      return {
        monthIndex: date.getMonth(),
        label: start.toLocaleDateString([locale, "en"], { month: "short" }),
        subtitle: `${Math.ceil(((end.getTime() - start.getTime()) / DAY_MS) + 1)}d`,
        alt,
        isQuarterStart,
        style: `left: ${left}%; width: ${width}%;`,
      };
    },
    onMonthClick(monthIndex) {
      if (this.zoomMonth !== null && this.zoomMonth !== undefined) {
        return;
      }

      this.$emit("month-click", { month: monthIndex });
    },
    getItemStyle(item, row) {
      const leftPercent = getOffsetPercent(item.clippedStart, this.range);
      const widthPercent = getWidthPercent(item.clippedStart, item.clippedEnd, this.range);
      const statusColor = this.getStatusBorderColor(item.status);
      const softColor = mixWithWhite(row.color, item.canceled ? 0.76 : 0.82);
      const highlightColor = mixWithWhite(row.color, item.canceled ? 0.64 : 0.68);
      const background = item.canceled
        ? `repeating-linear-gradient(135deg, ${softColor}, ${softColor} 8px, rgba(255,255,255,0.94) 8px, rgba(255,255,255,0.94) 14px)`
        : `linear-gradient(135deg, ${softColor} 0%, ${highlightColor} 100%)`;
      const textColor = row.textColor;
      const opacity = item.canceled ? 0.7 : 1;
      const width = item.isSingleDay ? "26px" : `max(calc(${widthPercent}% - 6px), 22px)`;
      const left = item.isSingleDay ? `calc(${leftPercent}% - 13px)` : `${leftPercent}%`;
      const top = (item.lane * LANE_HEIGHT) + (item.isSingleDay ? 8 : 10);
      const border = statusColor
        ? `1px solid ${alphaColor(statusColor, 0.55)}`
        : `1px solid ${alphaColor(row.color, 0.24)}`;
      const shadow = item.isSingleDay
        ? `0 8px 18px ${alphaColor(row.color, 0.18)}`
        : (statusColor
          ? `inset 3px 0 0 ${statusColor}, 0 10px 18px rgba(23, 50, 77, 0.08)`
          : "0 10px 18px rgba(23, 50, 77, 0.08)");
      const backgroundForItem = item.isSingleDay
        ? `radial-gradient(circle at 50% 45%, ${mixWithWhite(row.color, 0.7)} 0%, ${softColor} 72%)`
        : background;

      return [
        `left: ${left}`,
        `top: ${top}px`,
        `width: ${width}`,
        `background: ${backgroundForItem}`,
        `color: ${textColor}`,
        `border: ${border}`,
        `box-shadow: ${shadow}`,
        `opacity: ${opacity}`,
      ].join("; ");
    },
    getStatusBorderColor(status) {
      switch (status) {
        case 1:
          return "#2E7D32";
        case 2:
          return "#EF6C00";
        case 3:
          return "#1565C0";
        default:
          return null;
      }
    },
    showItemLabel(item) {
      if (item.isSingleDay) {
        return false;
      }

      const widthPercent = getWidthPercent(item.clippedStart, item.clippedEnd, this.range);
      return (MIN_TIMELINE_WIDTH * widthPercent) / 100 >= 56;
    },
    resolveStatusLabel(status) {
      const statusOption = (this.statusOptions || []).find((item) => item.id === status);
      return statusOption ? statusOption.label : "";
    },
    resolveModeLabel(mode) {
      const modeOption = (this.modeOptions || []).find((item) => item.id === mode);
      return modeOption ? modeOption.label : "";
    },
    formatDateLabel(value) {
      const date = parseIsoDate(value);
      if (!date) {
        return "";
      }

      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "en";
      return date.toLocaleDateString([locale, "en"], { year: "numeric", month: "2-digit", day: "2-digit" });
    },
    formatItemDates(item) {
      if (item.realizationStart && item.realizationEnd && item.realizationStart === item.realizationEnd) {
        return `${this.formatDateLabel(item.realizationStart)}`;
      }

      return `${this.formatDateLabel(item.realizationStart)} → ${this.formatDateLabel(item.realizationEnd)}`;
    },
    getItemAriaLabel(item, row) {
      return `${item.name}, ${row.name}, ${this.formatItemDates(item)}`;
    },
  },
};
</script>
